'use client';

import { HomepageContent } from '@/app/(site)/(home)/v2/lib/fetch-content';
import { sanityImageUrlFor } from '@/content/sanity/sanity-image-url-for/sanity-image-url-for';
import styled from '@emotion/styled';
import { List, ListItem, Typography } from '@mui/material';
import { PortableText } from '@portabletext/react';
import Image from 'next/image';
import Link from 'next/link';

type FeaturedPastExperience = HomepageContent['featuredPastExperiences'][number];

const PageDiv = styled.div`
  width: 100%;
  height: auto;
  margin: 0 auto;
  max-width: 900px;
  padding: 40px 10% 120px;
  @media (max-width: 768px) {
    padding: 24px 20px 120px;
  }
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 32px;
  color: #251628;
  font-family: var(--font-work-sans);
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const HeroImage = styled(Image)`
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 24px;
`;

const Title = styled(Typography)`
  font-family: var(--font-cooper-light);
  font-style: normal;
  font-weight: 300;
  font-size: 57px;
  line-height: 109.3%;
  color: #231727;
  margin-top: 32px;
  @media (max-width: 768px) {
    font-size: 40px;
    font-weight: 400;
  }
`;

const HostedBy = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: italic;
  font-weight: 500;
  font-size: 18px;
  color: #4d66b1;
  margin-top: 12px;
  margin-bottom: 32px;
`;

const Description = styled.div`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 159.3%;
  color: #251628;
`;

interface ArchiveExperienceDetailProps {
  experience: FeaturedPastExperience;
}

const ArchiveExperienceDetail = ({ experience }: ArchiveExperienceDetailProps) => {
  const { title, host, mainImage, shortDescription } = experience;

  const imageUrl = mainImage
    ? sanityImageUrlFor(mainImage).width(1600).height(900).url()
    : undefined;

  return (
    <PageDiv>
      <BackLink href="/explore">← Back to experiences</BackLink>
      {imageUrl && <HeroImage src={imageUrl} alt={title} width={1600} height={900} priority />}
      <Title>{title}</Title>
      {host && <HostedBy>Hosted by {host}</HostedBy>}
      {shortDescription && (
        <Description>
          <PortableText
            value={shortDescription}
            components={{
              block: {
                h1: ({ children }) => <Typography variant="h1">{children}</Typography>,
                h2: ({ children }) => <Typography variant="h2">{children}</Typography>,
                h3: ({ children }) => <Typography variant="h3">{children}</Typography>,
                h4: ({ children }) => <Typography variant="h4">{children}</Typography>,
                h5: ({ children }) => <Typography variant="h5">{children}</Typography>,
                h6: ({ children }) => <Typography variant="h6">{children}</Typography>,
                normal: ({ children }) => <Typography>{children}</Typography>,
              },
              list: ({ children }) => <List>{children}</List>,
              listItem: ({ children }) => <ListItem>{children}</ListItem>,
            }}
          />
        </Description>
      )}
    </PageDiv>
  );
};

export default ArchiveExperienceDetail;
